
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, ArrowLeft, User, Phone, BadgePercent } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// Mock data for customer search
const mockCustomers = [
  { id: 1, name: "Ahmad Bin Ali", phone: "013-3456789", idNumber: "880512-10-5543", memberType: "Regular", coopId: "", lastVisit: "2025-05-01", totalSpent: 1250.90 },
  { id: 2, name: "Siti Aminah", phone: "019-8765432", idNumber: "900623-14-5678", memberType: "Premium", coopId: "", lastVisit: "2025-05-10", totalSpent: 3456.75 },
  { id: 3, name: "David Lee", phone: "012-3456789", idNumber: "870304-08-1234", memberType: "Regular", coopId: "", lastVisit: "2025-05-05", totalSpent: 780.45 },
  { id: 4, name: "Raj Kumar", phone: "014-7890123", idNumber: "850718-12-5678", memberType: "Premium", coopId: "COOP-1234", lastVisit: "2025-05-02", totalSpent: 2345.60 },
  { id: 5, name: "Sarah Johnson", phone: "016-8901234", idNumber: "910825-04-3456", memberType: "Regular", coopId: "COOP-5678", lastVisit: "2025-05-08", totalSpent: 1678.30 }
];

const CustomerSearch = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(mockCustomers);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    
    // Simulating search delay
    setTimeout(() => {
      const results = mockCustomers.filter(customer => 
        customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.phone.includes(searchQuery) ||
        customer.idNumber.includes(searchQuery) ||
        customer.coopId.includes(searchQuery)
      );
      
      setSearchResults(results);
      setIsSearching(false);
      
      if (results.length === 0) {
        toast({
          title: "No customers found",
          description: "Try a different search term or create a new customer",
          variant: "destructive",
        });
      }
    }, 500);
  };

  const handleSelectCustomer = (customer: typeof mockCustomers[0]) => {
    toast({
      title: "Customer selected",
      description: `${customer.name} has been selected for this transaction`,
    });
    // In a real app, you would set this customer in the context or state
    navigate("/dashboard/posv2");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate("/dashboard/posv2")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Find Customer</h1>
        </div>
      </div>
      
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-md">Search by ID Number, Phone or Coop Member ID</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex items-center space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search customers..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={isSearching}>
              {isSearching ? "Searching..." : "Search"}
            </Button>
            <Button type="button" variant="outline">
              Create New Customer
            </Button>
          </form>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>ID Number</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Member Type</TableHead>
                <TableHead>Coop ID</TableHead>
                <TableHead>Last Visit</TableHead>
                <TableHead>Total Spent</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {searchResults.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-4 text-muted-foreground">
                    No customers found. Try a different search term.
                  </TableCell>
                </TableRow>
              ) : (
                searchResults.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-medium flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {customer.name}
                    </TableCell>
                    <TableCell>{customer.idNumber}</TableCell>
                    <TableCell className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {customer.phone}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <BadgePercent className="h-3 w-3" />
                        {customer.memberType}
                      </div>
                    </TableCell>
                    <TableCell>{customer.coopId || "-"}</TableCell>
                    <TableCell>{customer.lastVisit}</TableCell>
                    <TableCell>RM {customer.totalSpent.toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => handleSelectCustomer(customer)}>
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerSearch;
